<template>
  <el-row>
    <el-col>
      <div
        v-if="isLoading"
        v-loading="isLoading"
        class="mt-5"
      />
      <el-card
        v-else
        v-loading="isLoading"
        shadow="never"
      >
        <template v-if="consignment.edi">
          <div class="header-container pb-2">
            <div class="header-detail-container">
              <el-row :gutter="15">
                <el-col :span="6">
                  <p>
                    <b>Manifest</b><br>
                    {{ $_.get(consignment, 'edi.import_manifest_no', '-') }}
                  </p>

                  <p>
                    <b>Manifest Name</b><br>
                    {{ $_.get(consignment, 'edi.import_file_name', '-') }}
                  </p>
                </el-col>

                <el-col :span="6">
                  <p>
                    <b>Status</b><br>
                    {{ exportStatusText }}
                  </p>

                  <p>
                    <b>Locked</b><br>
                    {{ ediStatusText }}
                  </p>
                </el-col>

                <el-col :span="6">
                  <p>
                    <b>Created Time</b><br>
                    {{ formatDate($_.get(consignment, 'edi.import_stime')) }}
                  </p>

                  <p>
                    <b>Export Time</b><br>
                    {{ formatDate($_.get(consignment, 'edi.export_etime')) }}
                  </p>
                </el-col>

                <el-col :span="6">
                  <p>
                    <b>Consignments</b><br>
                    {{ ediConsignmentCount }}
                  </p>

                  <p>
                    <b>Carrier</b><br>
                    {{ $_.get(consignment, 'edi.carrier.name', '-') }}
                  </p>
                </el-col>
              </el-row>
            </div>
          </div>

          <ConsignmentTable
            :consignments="consignment.edi.consignments"
          />
        </template>
        <el-alert
          v-else
          title="No Manifest Found"
          type="error"
          :closable="false"
          center
        />
      </el-card>
      <Empty
        v-if="(!consignment || !consignment.edi) && !isLoading"
        description="There is no manifest for this consignment."
      />
    </el-col>
  </el-row>
</template>

<script>
import ConsignmentTable from '@/components/Application/Freight/EDI/ConsignmentTable'
import Request, { GET } from '@/plugins/sdk/Request'
import Empty from '@/components/Empty'

export default {
  components: {
    ConsignmentTable,
    Empty
  },
  data () {
    return {
      title: 'Manifest',
      breadcrumb: {
        name: 'Manifest',
        route: { name: 'freight-consignments-v3-consignment-manifest', params: { consignmentId: this.$route.params.consignmentId } }
      },
      isLoading: true,
      consignment: null,
      actions: [
        {
          name: 'Print',
          icon: ['fal', 'print'],
          event: this.printManifest
        },
        {
          name: 'Download EDI',
          icon: ['fal', 'download'],
          event: this.downloadEDIFile
        },
        {
          name: 'Resend',
          icon: ['fal', 'upload'],
          event: () => this.setEDIStatus('resend')
        },
        {
          name: 'Regenerate',
          icon: ['fal', 'repeat'],
          event: () => this.setEDIStatus('regenerate')
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: () => this.loadConsignment()
        }
      ],
      loading: true
    }
  },
  computed: {
    ediReference () {
      if (!this.consignment || !this.consignment.edi) {
        return '-'
      }

      return this.consignment.edi.import_manifest_no || this.consignment.edi.import_file_name || `EDI ${this.consignment.edi.id}`
    },
    exportStatusText () {
      return this.getExportStatus(this.$_.get(this.consignment, 'edi.export_status'))
    },
    ediStatusText () {
      return this.getEDIStatus(this.$_.get(this.consignment, 'edi.edi_status'))
    },
    ediConsignmentCount () {
      return this.$_.get(this.consignment, 'edi.consignments.length', this.$_.get(this.consignment, 'edi.import_cons', 0))
    }
  },
  mounted () {
    this.loadConsignment()
  },
  methods: {
    formatDate (value) {
      if (!value) {
        return '-'
      }

      const momentValue = this.$moment(value)
      return momentValue.isValid() ? momentValue.format('llll') : '-'
    },
    getExportStatus (value) {
      switch (value) {
        case 0:
          return 'Pending'
        case 1:
          return 'Pending'
        case 2:
          return 'File Created'
        case 3:
          return 'Success'
        case 4:
          return 'Failure'
        case 5:
          return 'Failure'
        default:
          return '-'
      }
    },
    getEDIStatus (value) {
      switch (value) {
        case 1:
          return 'Locked'
        default:
          return '-'
      }
    },
    loadConsignment () {
      this
        .$sdk
        .consignment
        .read(this.$route.params.consignmentId, request => {
          request.include = [
            'edi',
            'edi.file',
            'edi.consignments.service'
          ]
        })
        .then(res => { this.consignment = res })
        .finally(() => { this.isLoading = false })
    },
    downloadEDIFile () {
      const edi = this.$_.get(this.consignment, 'edi')

      if (!edi || !edi.file || !edi.file.id) {
        this.$message.warning('No file is available for this EDI record')
        return
      }

      this.$file.download(edi.file.id)
    },
    async setEDIStatus (status) {
      this.isLoading = true
      switch (status) {
        case 'regenerate':
          this.consignment.edi.export_status = 0
          await this.consignment.edi.update()
          break
        case 'resend':
          this.consignment.edi.export_status = 2
          await this.consignment.edi.update()
          break
        case 'unlock':
          this.consignment.edi.edi_status = 0
          await this.consignment.edi.update()
          break
      }
      this.isLoading = false
    },
    async printManifest () {
      const request = Object.assign(new Request(), {
        _pathname: `/legacy/manifest?consignment=${this.$route.params.consignmentId}`,
        method: GET
      })
      let res = await fetch(await request.build())
      let data = await res.json()

      if (data.manifest_pdf) {
        window.open(atob(data.manifest_pdf), '_blank')
      } else {
        this.$message({ message: 'No Manifest Found', type: 'error' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.header-container {
  display: flex;
  align-items: center;

  .header-detail-container {
    flex: 1;

    .header-code {
      cursor: pointer;
      font-size: 2rem;
      margin-bottom: 5px;
    }
  }
}
</style>
